/**
 * Login page for Aegix One.
 *
 * Provides social login (Google, Apple, Microsoft), passkey/WebAuthn,
 * and traditional email/password authentication for parents and guardians.
 */

import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  chakra,
  Flex,
  Text,
  Input,
  InputGroup,
  Image,
  VStack,
  Separator,
  Link,
  IconButton,
} from '@chakra-ui/react';
import { HiEye, HiEyeSlash } from 'react-icons/hi2';
import { Button, SelectInput } from '@themes/aegix';
import { useColorMode } from '@/components/ui/color-mode';
import logoLight from '@/assets/aegix-one-logo-light.svg';
import logoDark from '@/assets/aegix-one-logo-dark.svg';
import { SocialLoginButtons } from '@/components/SocialLoginButtons';
import { PasskeyButton } from '@/components/PasskeyButton';
import { loginSchema, type LoginFormValues } from '@/validation/auth';
import { useLoginForm } from '@/hooks/useLoginForm';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { LANGUAGE_OPTIONS } from '@/i18n/config';

export default function Login() {
  const { t, i18n } = useTranslation();
  useDocumentTitle(t('login.signIn'));
  const { colorMode } = useColorMode();
  const { error, setError, loading, showPassword, setShowPassword, handleSubmit: onSubmit } = useLoginForm();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  });

  return (
    <Flex
      height="100vh"
      alignItems="center"
      justifyContent="center"
      bg="surface.primary"
      position="relative"
    >
      <Box
        position="absolute"
        top={{ base: 4, md: 6 }}
        right={{ base: 4, md: 6 }}
        w="150px"
        zIndex={10}
      >
        <SelectInput
          name="language"
          option={LANGUAGE_OPTIONS}
          value={i18n.language ? i18n.language.split('-')[0] : 'en'}
          onChange={(val) => {
            void i18n.changeLanguage(val);
          }}
          e2eTitle="login-language-select"
        />
      </Box>

      <Box
        maxW={{ base: '100%', sm: '400px' }}
        w="100%"
        p={8}
        borderWidth="1px"
        borderColor="border.primary"
        borderRadius="lg"
        bg="surface.primary"
        boxShadow="0px 2.5px 4px -1px rgba(0, 0, 0, 0.15)"
      >
        <VStack gap={6}>
          <Image
            src={colorMode === 'dark' ? logoDark : logoLight}
            alt="Aegix One"
            w="200px"
          />

          <Text fontSize="sm" color="text.secondary" textAlign="center">
            {t('login.signInSubtitle')}
          </Text>

          {/* Social login buttons */}
          <SocialLoginButtons />

          {/* Divider */}
          <Flex align="center" w="100%" gap={3}>
            <Separator flex="1" />
            <Text fontSize="xs" color="text.tertiary" whiteSpace="nowrap">
              {t('login.or')}
            </Text>
            <Separator flex="1" />
          </Flex>

          {/* Passkey login */}
          <PasskeyButton
            onSuccess={() => {
              // Auth context will handle redirect
            }}
            onError={(msg) => setError(msg)}
          />

          {/* Divider */}
          <Flex align="center" w="100%" gap={3}>
            <Separator flex="1" />
            <Text fontSize="xs" color="text.tertiary" whiteSpace="nowrap">
              {t('login.or')}
            </Text>
            <Separator flex="1" />
          </Flex>

          {/* Email/password form */}
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <VStack gap={4} w="100%">
              <Box w="100%">
                <chakra.label htmlFor="login-email" fontSize="sm" color="text.secondary" mb={1} display="block">
                  {t('login.emailLabel')}
                </chakra.label>
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'login-email-error' : undefined}
                  {...register('email')}
                  placeholder={t('login.emailPlaceholder')}
                  size="md"
                />
                {errors.email && (
                  <Text id="login-email-error" role="alert" color="red.500" fontSize="sm">
                    {t(errors.email.message!)}
                  </Text>
                )}
              </Box>

              <Box w="100%">
                <chakra.label htmlFor="login-password" fontSize="sm" color="text.secondary" mb={1} display="block">
                  {t('login.passwordLabel')}
                </chakra.label>
                <InputGroup
                  endElement={
                    <IconButton
                      aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      size="xs"
                      color="text.secondary"
                    >
                      {showPassword ? <HiEyeSlash /> : <HiEye />}
                    </IconButton>
                  }
                >
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? 'login-password-error' : undefined}
                    {...register('password')}
                    placeholder={t('login.passwordPlaceholder')}
                    size="md"
                  />
                </InputGroup>
                {errors.password && (
                  <Text id="login-password-error" role="alert" color="red.500" fontSize="sm">
                    {t(errors.password.message!)}
                  </Text>
                )}
              </Box>

              <Flex w="100%" justifyContent="flex-end">
                <Link asChild color="blue.500" fontSize="sm" fontWeight="medium">
                  <RouterLink to="/reset-password">{t('login.forgotPassword')}</RouterLink>
                </Link>
              </Flex>

              {error && (
                <Text role="alert" aria-live="assertive" color="red.500" fontSize="sm">
                  {error}
                </Text>
              )}

              <Button
                visual="primary"
                type="submit"
                disabled={loading || !isValid}
                style={{ width: '100%' }}
              >
                {loading ? t('login.signingIn') : t('login.signIn')}
              </Button>
            </VStack>
          </form>

          {/* Registration link */}
          <Text fontSize="sm" color="text.secondary">
            {t('login.noAccount')}{' '}
            <Link asChild color="blue.500" fontWeight="medium">
              <RouterLink to="/register">{t('login.createAccount')}</RouterLink>
            </Link>
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
}
